export type DesktopPlatform = 'linux' | 'macos' | 'windows';
export type UnlistenFn = () => void;
export type DesktopEvent<T> = { payload: T };
export type DesktopUpdateStatus =
  | { state: 'unsupported'; currentVersion: string }
  | { state: 'idle' | 'checking' | 'up-to-date'; currentVersion: string }
  | {
      state: 'available' | 'ready';
      currentVersion: string;
      version: string;
      notes: string;
      releaseDate: string | null;
    }
  | {
      state: 'downloading';
      currentVersion: string;
      version: string;
      percent: number;
      transferred: number;
      total: number;
    }
  | { state: 'error'; currentVersion: string; message: string }
  /** Updates come from Crowdex Cloud: this app is not linked to it. */
  | { state: 'unlinked'; currentVersion: string };

type DesktopBridge = {
  invoke(command: string, args?: Record<string, unknown>): Promise<unknown>;
  listen(event: string, listener: (payload: unknown) => void): UnlistenFn;
  platform: NodeJS.Platform;
  preferences?: () => Record<string, string>;
};

declare global {
  interface Window {
    crowdexDesktop?: DesktopBridge;
  }
}

function bridge(): DesktopBridge {
  const current = window.crowdexDesktop;
  if (!current) throw new Error('This command is only available in Crowdex Desktop.');
  return current;
}

export function isElectronDesktop(): boolean {
  return typeof window !== 'undefined' && window.crowdexDesktop !== undefined;
}

export function electronPlatform(): DesktopPlatform {
  const current = bridge().platform;
  return current === 'darwin' ? 'macos' : current === 'win32' ? 'windows' : 'linux';
}

export function electronInvoke<TResult>(
  command: string,
  args: Record<string, unknown> = {},
): Promise<TResult> {
  return bridge().invoke(command, args) as Promise<TResult>;
}

export async function listen<T>(
  event: string,
  listener: (event: DesktopEvent<T>) => void,
): Promise<UnlistenFn> {
  return bridge().listen(event, (payload) => listener({ payload: payload as T }));
}

export function openDirectoryDialog(): Promise<string | null> {
  return electronInvoke<string | null>('dialog_open');
}

export function desktopPreferences(): Record<string, string> | null {
  if (typeof window === 'undefined' || window.crowdexDesktop === undefined) return null;
  try {
    return window.crowdexDesktop.preferences?.() ?? null;
  } catch {
    return {};
  }
}

export function setDesktopPreference(key: string, value: string): Promise<void> {
  return electronInvoke<void>('preference_set', { key, value });
}

export function desktopUpdateStatus(): Promise<DesktopUpdateStatus> {
  return electronInvoke<DesktopUpdateStatus>('desktop_update_status');
}

export function checkForDesktopUpdate(): Promise<DesktopUpdateStatus> {
  return electronInvoke<DesktopUpdateStatus>('desktop_update_check');
}

export function downloadDesktopUpdate(): Promise<DesktopUpdateStatus> {
  return electronInvoke<DesktopUpdateStatus>('desktop_update_download');
}

export function installDesktopUpdate(): Promise<void> {
  return electronInvoke<void>('desktop_update_install');
}

export function subscribeDesktopUpdateStatus(
  listener: (status: DesktopUpdateStatus) => void,
): UnlistenFn {
  if (typeof window === 'undefined' || window.crowdexDesktop === undefined) return () => undefined;
  return window.crowdexDesktop.listen('desktop-update-status', (payload) =>
    listener(payload as DesktopUpdateStatus),
  );
}
